import type { AxiosInstance, AxiosResponse } from 'axios';

import { transformObjectToCamelCase, transformObjectToSnakeCase } from '@utilities';

import instantSurveyApi from './api';

const axios = instantSurveyApi as AxiosInstance;

interface CreatePreviewUrlPayload {
  preview_url?: string;
  quota_group_id?: number | string;
  quota_group_name?: string;
}

interface PreviewSessionPayload {
  id: string;
}

interface PreviewUrlPerQuotaGroupPayload {
  previewUrl?: string;
  quotaGroupId?: number | string;
  quotaGroupName?: string;
}

/**
 * Creates a preview session for an instant survey based on the provided selection criteria.
 * Optionally starts at a specific question
 */
export const createPreviewSession = async ({
  instantSurveyId,
  previewSelection,
  signal,
}: {
  instantSurveyId: number;
  previewSelection: {
    language?: string;
    quotaGroupId: number;
    startingQuestionId: number;
  };
  signal?: AbortSignal;
}): Promise<PreviewSessionPayload> => {
  return axios({
    data: transformObjectToSnakeCase(previewSelection),
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${instantSurveyId}/preview_session`,
  }).then(({ data }: AxiosResponse<PreviewSessionPayload>) => data);
};

/**
 * Creates a list of preview urls for every quota group id in a survey.
 * A preview url will be always be formatted as: '?surveyName=name&quotaGroupName=groupName&token=token'
 */
export const createPreviewUrls = async ({
  instantSurveyId,
  signal,
}: {
  instantSurveyId: number | string;
  signal?: AbortSignal;
}): Promise<PreviewUrlPerQuotaGroupPayload[]> => {
  return axios({
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${instantSurveyId}/shareable_preview_links`,
  }).then(({ data }: AxiosResponse<CreatePreviewUrlPayload[]>) => {
    return transformObjectToCamelCase(data) as PreviewUrlPerQuotaGroupPayload[];
  });
};
